import React, { useEffect, useMemo, useRef, useState } from "react";

// region Imports - External libraries
import _ from "lodash";
import * as dateFns from "date-fns";
import Loading from "react-fullscreen-loading";
// endregion Imports - External libraries
// region Imports - Shared
import { Travel } from "@shared/interfaces/travel.interface";
import { Vehicle } from "@shared/interfaces/vehicle.interface";
import { IParamQueryValues } from "@shared/interfaces/filter.interface";
import { FilterApplicationTypesID } from "@shared/constants/filter-application-types.enum";
import { Regional } from "@shared/entities/reg_regionals.entity";
import { Group } from "@shared/entities/reg_groups.entity";
import { VehicleType } from "@shared/interfaces/vehicle-type.interface";
import { Driver } from "@shared/interfaces/driver.interface";
import { Plant } from "@shared/entities/reg_plants.entity";
import { CoordinateCommand } from "@shared/interfaces/coordinate-command.interface";
import { VehicleStatesTypesID } from "@shared/constants/vehicle-states-types.enum";
import { InternalCodeErrors } from "@shared/constants/internal-code-errors.enum";
import { ParamNameTypes } from "@shared/constants/param-name-types.enum";
import { ReportTypes } from "@shared/constants/report-types.enum";
// endregion Imports - Shared
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import {
  FilterMessages,
  GlobalMessages,
  MenuMessages,
  ToastMessages,
  TravelHistoryMessages,
  VehiclesStatesTypesMessages
} from "@shared/languages/interfaces";
import { TpeVehicleState } from "@shared/entities/tpe_vehicles_states_types.entity";
// endregion Imports - Languages
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Utils
import utils from "@utils/useful-functions";
// endregion Imports - Utils
// region Imports - Hooks
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Components
import ModalFormScheduledReportDescription from "@components/Modal/FormScheduledReportDescription";
import GenericQueryFilter, {
  IDateRangeOptions,
  IDateRangeValues,
  IMultipleSelectionOptions,
  INumberRangeOptions,
  IOnFilterReturn
} from "@organisms/GenericQueryFilter";
// endregion Imports - Components

// region Interfaces
interface IProps {
  open: boolean;
  onClose: () => void;
  initialTravelData?: Travel;
  initialDateRange?: IDateRangeValues;
  reportType: ReportTypes;
  onFilter: (
    numberOfFilteredOptions: number,
    travelAnalyticsData: CoordinateCommand[]
  ) => void;
  exportAnalyticReport: {
    openModal: boolean;
    onCloseModal: () => void;
  }
}
// endregion Interfaces

const QueryFilterCoordinatesTravelHistory: React.FC<IProps> = ({
  open,
  onClose,
  initialTravelData,
  initialDateRange,
  reportType,
  onFilter,
  exportAnalyticReport
}) => {

  // region Hooks
  const { t } = useTranslation();
  const { addToast } = useToast();
  // endregion Hooks
  // region States
  const [optionsVehicleFilter, setOptionsVehicleFilter] = useState<Vehicle[]>([] as Array<Vehicle>);
  const [optionsVehicleTypeFilter, setOptionsVehicleTypeFilter] = useState<VehicleType[]>([] as Array<VehicleType>);
  const [optionsPlantFilter, setOptionsPlantFilter] = useState<Plant[]>([] as Array<Plant>);
  const [optionsRegionalFilter, setOptionsRegionalFilter] = useState<Regional[]>([] as Array<Regional>);
  const [optionsGroupFilter, setOptionsGroupFilter] = useState<Group[]>([] as Array<Group>);
  const [optionsDriverFilter, setOptionsDriverFilter] = useState<Driver[]>([] as Array<Driver>);
  const [optionsTravelFilter, setOptionsTravelFilter] = useState<Travel[]>([] as Array<Travel>);
  const [optionsVehicleStateTypeFilter, setOptionsVehicleStateTypeFilter] = useState([] as Array<TpeVehicleState>);

  const [loadingVehicleFilter, setLoadingVehicleFilter] = useState(true);
  const [loadingVehicleTypeFilter, setLoadingVehicleTypeFilter] = useState(true);
  const [loadingPlantFilter, setLoadingPlantFilter] = useState(true);
  const [loadingRegionalFilter, setLoadingRegionalFilter] = useState(true);
  const [loadingGroupFilter, setLoadingGroupFilter] = useState(true);
  const [loadingDriverFilter, setLoadingDriverFilter] = useState(false);
  const [loadingTravelFilter, setLoadingTravelFilter] = useState(false);
  const [loadingVehicleStateTypeFilter, setLoadingVehicleStateTypeFilter] = useState(false);

  const [loadingCoordinates, setLoadingCoordinates] = useState(false);

  // endregion States
  // region Constants

  // Param names
  const paramNameStartDate = ParamNameTypes.START_DATE;
  const paramNameEndDate = ParamNameTypes.END_DATE;
  const paramNameVehicle = ParamNameTypes.ID_VEHICLES;
  const paramNameTravel = ParamNameTypes.ID_TRAVELS;
  const paramNameDriver = ParamNameTypes.ID_DRIVERS;
  const paramNameVehicleType = ParamNameTypes.ID_VEHICLE_TYPES;
  const paramNamePlant = ParamNameTypes.ID_PLANTS;
  const paramNameGroup = ParamNameTypes.ID_GROUPS;
  const paramNameRegional = ParamNameTypes.ID_REGIONALS;
  const paramNameVehicleStateType = ParamNameTypes.ID_VEHICLE_STATE_TYPES;
  const paramNameStartSpeed = ParamNameTypes.START_SPEED;
  const paramNameEndSpeed = ParamNameTypes.END_SPEED;

  const regionalFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingRegionalFilter,
    labelName: t(MenuMessages.regionals),
    paramName: paramNameRegional,
    values: optionsRegionalFilter.map(
      (regional) => ({
        value: regional.id_regional!,
        label: `${regional.code} - ${regional.name}`
      })
    )
  }), [loadingRegionalFilter, optionsRegionalFilter, paramNameRegional, t]);

  const groupFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingGroupFilter,
    labelName: t(MenuMessages.groups),
    paramName: paramNameGroup,
    values: optionsGroupFilter.map(
      (group) => ({
        value: group.id_group!,
        label: `${group.code} - ${group.name}`
      })
    )
  }), [loadingGroupFilter, optionsGroupFilter, paramNameGroup, t]);

  const vehicleFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingVehicleFilter,
    labelName: t(GlobalMessages.vehicle),
    paramName: paramNameVehicle,
    // eslint-disable-next-line max-len
    values: optionsVehicleFilter.map((vehicle) => ({ value: vehicle.id_vehicle, label: `${vehicle.code} - ${vehicle.description}` }))
  }), [loadingVehicleFilter, optionsVehicleFilter, paramNameVehicle, t]);

  const travelFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingTravelFilter,
    labelName: t(TravelHistoryMessages.travels),
    paramName: paramNameTravel,
    values: optionsTravelFilter.map((travel) => ({
      value: travel.id_travel!,
      label: utils.formatAutoCompleteTravelLabel(travel)
    })),
    refreshOptions: {
      refresh: (currentValues) => {

        const startDate = currentValues.find(
          (param) => param.paramName === paramNameStartDate
        )?.paramValue as string;
        const endDate = currentValues.find(
          (param) => param.paramName === paramNameEndDate
        )?.paramValue as string;
        const vehicles = currentValues.find(
          (param) => param.paramName === paramNameVehicle
        )?.paramValue ?? [];

        if (!!startDate && !!endDate && !!vehicles) {
          currentSelectedVehicles.current = Array.isArray(vehicles) ? vehicles : [vehicles];
          currentDateRange.current = { startDate, endDate };

          updateTravelOptionsAccordingByVehicleAndDateRange().then();
        }
      },
      dependentParamNames: [paramNameVehicle, paramNameStartDate, paramNameEndDate]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [loadingTravelFilter, optionsTravelFilter, paramNameEndDate, paramNameStartDate, paramNameTravel, paramNameVehicle, t]);

  const driverFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingDriverFilter,
    labelName: t(MenuMessages.drivers),
    paramName: paramNameDriver,
    values: optionsDriverFilter.map((driver) => ({
      value: driver.id_driver!,
      label: `${driver.name} - ${driver.cpf}`
    }))
  }), [loadingDriverFilter, optionsDriverFilter, paramNameDriver, t]);

  const vehicleTypeFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingVehicleTypeFilter,
    labelName: t(FilterMessages.optionVehicleTypes),
    paramName: paramNameVehicleType,
    values: optionsVehicleTypeFilter.map((vehicleType) => ({ value: vehicleType.id_vehicle_type!, label: vehicleType.description }))
  }), [loadingVehicleTypeFilter, optionsVehicleTypeFilter, paramNameVehicleType, t]);

  const plantFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingPlantFilter,
    labelName: t(FilterMessages.optionPlants),
    paramName: paramNamePlant,
    values: optionsPlantFilter.map(
      (plant) => ({
        value: plant!.location!.id_location!,
        label: plant.name!
      })
    )
  }), [loadingPlantFilter, optionsPlantFilter, paramNamePlant, t]);

  const vehicleStateTypeFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingVehicleStateTypeFilter,
    labelName: t(FilterMessages.optionVehicleStateTypes),
    paramName: paramNameVehicleStateType,
    values: optionsVehicleStateTypeFilter.map(
      (vehicleStateType) => ({
        value: vehicleStateType.id_vehicle_state_type!,
        label: t(VehiclesStatesTypesMessages[vehicleStateType.id_vehicle_state_type!])
      })
    )
  }), [loadingVehicleStateTypeFilter, optionsVehicleStateTypeFilter, paramNameVehicleStateType, t]);

  const defaultStartDate = dateFns.subDays(new Date(), 1);

  defaultStartDate.setHours(0, 0, 0, 0);

  const defaultEndDate = new Date();

  defaultEndDate.setHours(23, 59, 59, 999);

  const dateRangeOptions: IDateRangeOptions = {
    labelName: t(FilterMessages.dateRangeLabel),
    paramNameStartDate,
    paramNameEndDate,
    defaultValues: {
      startDate: initialDateRange?.startDate ?? utils.convertDateToISOWithTimezone(defaultStartDate),
      endDate: initialDateRange?.endDate ?? utils.convertDateToISOWithTimezone(defaultEndDate)
    },
    maxRange: "30D"
  };

  const speedRangeOptions: INumberRangeOptions = {
    labelName: t(FilterMessages.optionSpeed),
    paramNameStartValue: paramNameStartSpeed,
    paramNameEndValue: paramNameEndSpeed
  };

  // Initial params data
  const initialParamsData: IParamQueryValues[] | undefined = initialTravelData ? [
    { paramName: paramNameStartDate, paramValue: dateRangeOptions.defaultValues.startDate },
    { paramName: paramNameEndDate, paramValue: dateRangeOptions.defaultValues.endDate },
    { paramName: paramNameVehicle, paramValue: [initialTravelData!.vehicle.id_vehicle] },
    { paramName: paramNameTravel, paramValue: [initialTravelData!.id_travel] }
  ] : undefined;
  // endregion Constants
  // region Refs
  const currentDateRange = useRef<IDateRangeValues>({
    startDate: dateRangeOptions.defaultValues.startDate,
    endDate: dateRangeOptions.defaultValues.endDate
  });
  const currentSelectedVehicles = useRef<string[]>([]);
  const appliedFilter = useRef<IParamQueryValues[]>([]);
  // endregion Refs
  // region Functions

  /**
   * Update travel options according to the selected vehicle and date range.
   */
  const updateTravelOptionsAccordingByVehicleAndDateRange = async () => {

    setLoadingTravelFilter(true);

    try {

      if (currentSelectedVehicles.current.length === 0) {

        // Set 100ms delay to GenericQueryFilter can get the loading state
        await new Promise((resolve) => setTimeout(resolve, 100));

        setOptionsTravelFilter([] as Travel[]);

        return;
      }

      const { data } = await api.post("travels/get-filtered-by", {
        idVehicles: currentSelectedVehicles.current,
        startDate: currentDateRange.current.startDate,
        finishDate: currentDateRange.current.endDate,
        finished: true,
        vehicleStates: VehicleStatesTypesID.A_CAMINHO
      });

      if (data.status === "success") setOptionsTravelFilter(data.result);
      else setOptionsTravelFilter([] as Travel[]);
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingTravelFilter(false);
    }
  };

  /**
   * Load vehicle options from the API.
   */
  const loadVehicleOptions = async () => {

    try {

      // Get all vehicles
      const { data } = await api.get("vehicles/read", { params: { linkedHardware: true, onlyLabel: true } });

      if (data.status === "success") setOptionsVehicleFilter(data.result);
      else addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });

    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoadingVehicleFilter(false);
    }
  };

  /**
   * Load vehicle type options from the API.
   */
  const loadVehicleTypeOptions = async () => {
    try {

      // Get all alert types
      const { data } = await api.get("vehicles/types/read", { params: { translate: true } });

      if (data.status === "success") {
        setOptionsVehicleTypeFilter(data.result);

        return;
      }

      setOptionsVehicleTypeFilter([]);

    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingVehicleTypeFilter(false);
    }
  };

  /**
   * Load regional options from the API.
   */
  const loadRegionalOptions = async () => {
    try {

      // Get all regionals
      const { data } = await api.get("regionals/read");

      if (data.status === "success") setOptionsRegionalFilter(data.result as Regional[]);
      else setOptionsRegionalFilter([]);

    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingRegionalFilter(false);
    }
  };

  /**
   * Load plant options from the API.
   */
  const loadPlantOptions = async () => {
    try {

      // Get all plants
      const { data } = await api.get("plants/read", { params: { linkedLocation: true } });

      if (data.status === "success") setOptionsPlantFilter(data.result);
      else setOptionsPlantFilter([]);

    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingPlantFilter(false);
    }
  };

  /**
   * Load driver options from the API.
   */
  const loadDriverOptions = async () => {
    try {

      // Get all drivers
      const { data } = await api.get("drivers/read");

      if (data.status === "success") setOptionsDriverFilter(data.result as Driver[]);
      else setOptionsDriverFilter([]);

    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingDriverFilter(false);
    }
  };

  /**
   * Load group options from the API.
   */
  const loadGroupOptions = async () => {
    try {

      // Get all regionals
      const { data } = await api.get("groups/read");

      if (data.status === "success") setOptionsGroupFilter(data.result as Group[]);
      else setOptionsGroupFilter([]);

    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingGroupFilter(false);
    }
  };

  /**
   * Load vehicle state type options from the API.
   */
  const loadVehicleStateTypeOptions = async () => {
    try {

      // Get all vehicle state types
      const { data } = await api.get("vehicles/get-all-state-types");

      if (data.status === "success") setOptionsVehicleStateTypeFilter(data.result);
      else setOptionsVehicleStateTypeFilter([]);

    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingVehicleStateTypeFilter(false);
    }
  };

  const handleFilter = async (paramsData: IParamQueryValues[]) => {

    setLoadingCoordinates(true);

    appliedFilter.current = paramsData;

    try {

      const mappedParams = paramsData.reduce((acc, param) => {
        acc[param.paramName] = param.paramValue;

        return acc;
      }, {} as { [key: string]: string | string[] });

      const { data } = await api.post("travels/get-coordinate-commands", {
        ...mappedParams,
        [ParamNameTypes.REPORT_TYPE]: reportType
      });

      const validParamsData = paramsData.filter((item) => !_.isEmpty(item.paramValue));

      const validParamsDataWithoutDate = validParamsData.filter(
        (item) => item.paramName !== paramNameStartDate && item.paramName !== paramNameEndDate
      );

      if (data.status === "alert" && data.errorCode === InternalCodeErrors.VERY_LARGE_FILTER) {
        const additionalFilterOptions : IParamQueryValues[] = [
          { paramName: ParamNameTypes.REPORT_TYPE, paramValue: reportType },
          { paramName: ParamNameTypes.UTC_OFFSET, paramValue: (utils.getUtcOffset()).toString() }
        ];

        return { status: "scheduledReport", additionalFilterOptions } as IOnFilterReturn;
      }

      if (data.status !== "success") addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });

      onFilter(
        validParamsDataWithoutDate.length,
        data.result ?? [] as CoordinateCommand[]
      );

      return { status: "success" } as IOnFilterReturn;
    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });

      return { status: "error" } as IOnFilterReturn;
    } finally {
      setLoadingCoordinates(false);
    }
  };
  // endregion Functions
  // region Effects

  // Load data from the API when the report type changes
  useEffect(() => {

    if (!_.isEmpty(appliedFilter.current)) handleFilter(appliedFilter.current).then();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType]);

  // Load data from the API when the initial params data is set
  useEffect(() => {
    if (initialParamsData) {
      const vehicleTravel = initialParamsData.find((param) => param.paramName === paramNameVehicle);

      currentSelectedVehicles.current = vehicleTravel!.paramValue as string[];

      handleFilter(initialParamsData).then();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load options from the API
  useEffect(() => {

    Promise.all([
      updateTravelOptionsAccordingByVehicleAndDateRange(),
      loadVehicleOptions(),
      loadVehicleTypeOptions(),
      loadRegionalOptions(),
      loadGroupOptions(),
      loadPlantOptions(),
      loadDriverOptions(),
      loadVehicleStateTypeOptions()
    ]).then();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // endregion Effects

  return (
    <>
      <Loading loading={loadingCoordinates} />
      <GenericQueryFilter
        open={open}
        onClose={onClose}
        defaultAppliedFilterOptions={initialParamsData}
        applicationTypeID={FilterApplicationTypesID.COORDINATE_TRAVEL_HISTORY}
        onFilter={(paramsData) => handleFilter(paramsData)}
        multipleSelectionOptions={[
          vehicleFilterOptions,
          travelFilterOptions,
          vehicleTypeFilterOptions,
          driverFilterOptions,
          plantFilterOptions,
          regionalFilterOptions,
          groupFilterOptions,
          vehicleStateTypeFilterOptions
        ]}
        dateRangeOptions={dateRangeOptions}
        numberRangeOptions={speedRangeOptions}
      />
      <ModalFormScheduledReportDescription
        open={exportAnalyticReport.openModal}
        onClose={() => exportAnalyticReport.onCloseModal()}
        filter={{
          application_type_id: FilterApplicationTypesID.COORDINATE_TRAVEL_HISTORY,
          filter_options: appliedFilter.current
        }}
        additionalFilterOptions={[
          { paramName: ParamNameTypes.REPORT_TYPE, paramValue: reportType },
          { paramName: ParamNameTypes.UTC_OFFSET, paramValue: (utils.getUtcOffset()).toString() }
        ]}
      />
    </>
  );
};

export default QueryFilterCoordinatesTravelHistory;
